// Web Mercator math for the 2D map. Pure functions, no DOM.
//
// "World" coordinates are normalised Web Mercator: x, y in [0, 1], x east, y south
// (tile/screen convention). At zoom z the world is TILE_SIZE * 2^z CSS pixels wide.

export const TILE_SIZE = 256;
export const MAX_LAT = 85.0511287798;
const EARTH_CIRCUMFERENCE = 40075016.686;
const DEG = Math.PI / 180;

export function lonToX(lon: number): number {
  return (lon + 180) / 360;
}

export function latToY(lat: number): number {
  const l = lat > MAX_LAT ? MAX_LAT : lat < -MAX_LAT ? -MAX_LAT : lat;
  const s = Math.sin(l * DEG);
  return 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI);
}

export function xToLon(x: number): number {
  return x * 360 - 180;
}

export function yToLat(y: number): number {
  return Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) / DEG;
}

/** World width in CSS pixels at (fractional) zoom z. */
export function worldSize(zoom: number): number {
  return TILE_SIZE * Math.pow(2, zoom);
}

/** Ground metres per CSS pixel at latitude lat and zoom z. */
export function metersPerPixel(lat: number, zoom: number): number {
  return (EARTH_CIRCUMFERENCE * Math.cos(lat * DEG)) / worldSize(zoom);
}

/** Map view: centre in world units, fractional zoom, size in CSS pixels. */
export interface View {
  cx: number;
  cy: number;
  zoom: number;
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

export function worldToScreen(v: View, wx: number, wy: number, out: Point): Point {
  const ws = worldSize(v.zoom);
  out.x = (wx - v.cx) * ws + v.width / 2;
  out.y = (wy - v.cy) * ws + v.height / 2;
  return out;
}

export function screenToWorld(v: View, sx: number, sy: number, out: Point): Point {
  const ws = worldSize(v.zoom);
  out.x = v.cx + (sx - v.width / 2) / ws;
  out.y = v.cy + (sy - v.height / 2) / ws;
  return out;
}

/** Move the map content by (dx, dy) CSS pixels (content follows the finger). */
export function panBy(v: View, dx: number, dy: number): void {
  const ws = worldSize(v.zoom);
  v.cx -= dx / ws;
  v.cy -= dy / ws;
  clampCentre(v);
}

/** Set zoom so that the world point (wx, wy) ends up at screen point (sx, sy). */
export function placeWorldAt(v: View, wx: number, wy: number, sx: number, sy: number, zoom: number): void {
  v.zoom = zoom;
  const ws = worldSize(zoom);
  v.cx = wx - (sx - v.width / 2) / ws;
  v.cy = wy - (sy - v.height / 2) / ws;
  clampCentre(v);
}

/** Zoom to newZoom keeping the world point under screen point (sx, sy) fixed. */
export function zoomAround(v: View, sx: number, sy: number, newZoom: number): void {
  const ws = worldSize(v.zoom);
  const wx = v.cx + (sx - v.width / 2) / ws;
  const wy = v.cy + (sy - v.height / 2) / ws;
  placeWorldAt(v, wx, wy, sx, sy, newZoom);
}

function clampCentre(v: View): void {
  if (v.cy < 0) v.cy = 0;
  else if (v.cy > 1) v.cy = 1;
  v.cx = v.cx - Math.floor(v.cx);
}

/** Zoom level at which the world bbox [x0, y0, x1, y1] fits into width x height with padding (px). */
export function fitZoom(x0: number, y0: number, x1: number, y1: number, width: number, height: number, pad = 8): number {
  const w = Math.max(x1 - x0, 1e-9);
  const h = Math.max(y1 - y0, 1e-9);
  const zx = Math.log2(Math.max(width - 2 * pad, 1) / (w * TILE_SIZE));
  const zy = Math.log2(Math.max(height - 2 * pad, 1) / (h * TILE_SIZE));
  return Math.min(zx, zy);
}

/** Tile index range covering the view at integer tile zoom tz (x may exceed [0, 2^tz), wrap when requesting). */
export function tileRange(v: View, tz: number): { x0: number; y0: number; x1: number; y1: number } {
  const n = Math.pow(2, tz);
  const ws = worldSize(v.zoom);
  const hw = v.width / 2 / ws;
  const hh = v.height / 2 / ws;
  return {
    x0: Math.floor((v.cx - hw) * n),
    x1: Math.floor((v.cx + hw) * n),
    y0: Math.max(0, Math.floor((v.cy - hh) * n)),
    y1: Math.min(n - 1, Math.floor((v.cy + hh) * n)),
  };
}

/** "Nice" scale bar: largest 1/2/5 x 10^k metres not longer than maxPx. */
export function niceScale(metersPerPx: number, maxPx: number): { meters: number; px: number; label: string } {
  const maxM = metersPerPx * maxPx;
  const p = Math.pow(10, Math.floor(Math.log10(maxM)));
  const f = maxM / p;
  const m = (f >= 5 ? 5 : f >= 2 ? 2 : 1) * p;
  const label = m >= 1000 ? `${m / 1000} km` : `${m} m`;
  return { meters: m, px: m / metersPerPx, label };
}
